import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

const Places = () => {
  const navigate = useNavigate();

  const destinations = [
    {
      name: "Indore",
      description: "The commercial capital of Madhya Pradesh, known for its rich heritage and vibrant food culture",
      path: "/destinations/indore",
      attractions: [
        {
          name: "Rajwada Palace",
          description: "Historic royal palace showcasing Maratha architecture",
          path: "/destinations/indore/rajwada-palace"
        },
        {
          name: "Sarafa Bazaar",
          description: "Famous night food market with authentic Indori delicacies",
          path: "/destinations/indore/sarafa-bazaar"
        },
        {
          name: "Lal Bagh Palace",
          description: "Magnificent palace reflecting European architectural influences",
          path: "/destinations/indore/lal-bagh-palace"
        },
        {
          name: "Kanch Mandir",
          description: "Beautiful Jain temple adorned with intricate glasswork",
          path: "/destinations/indore/kanch-mandir"
        },
        {
          name: "Central Museum",
          description: "Rich collection of archaeological artifacts and cultural heritage",
          path: "/destinations/indore/central-museum"
        },
        {
          name: "Choral Dam",
          description: "Scenic reservoir perfect for nature lovers and photography",
          path: "/destinations/indore/choral-dam"
        }
      ]
    },
    {
      name: "Ujjain",
      description: "One of the seven sacred cities in Hinduism, famous for the Kumbh Mela",
      path: "/destinations/ujjain",
      attractions: [
        {
          name: "Mahakaleshwar Temple",
          description: "Sacred Jyotirlinga temple dedicated to Lord Shiva",
          path: "/destinations/ujjain/mahakaleshwar-temple"
        },
        {
          name: "Kal Bhairav Temple",
          description: "Ancient temple dedicated to the guardian deity of Ujjain",
          path: "/destinations/ujjain/kal-bhairav-temple"
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>All Places - Tourist Destinations & Attractions | Aao Ghumen</title>
        <meta 
          name="description" 
          content="Discover all tourist places and attractions in Madhya Pradesh. Complete list of destinations including Indore, Ujjain with detailed information about temples, palaces, museums, and heritage sites."
        />
        <meta 
          name="keywords" 
          content="tourist places, destinations, attractions, Indore, Ujjain, Madhya Pradesh, temples, palaces, heritage sites, travel guide"
        />
        <link rel="canonical" href="https://aaoghumen.com/places" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="All Places - Tourist Destinations & Attractions | Aao Ghumen" />
        <meta property="og:description" content="Complete directory of tourist places and attractions in Madhya Pradesh including Indore and Ujjain destinations." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://aaoghumen.com/places" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="All Places - Tourist Destinations & Attractions" />
        <meta name="twitter:description" content="Complete directory of tourist places and attractions in Madhya Pradesh including Indore and Ujjain destinations." />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TouristDestination",
            "name": "Madhya Pradesh Tourist Places",
            "description": "Complete directory of tourist destinations and attractions in Madhya Pradesh",
            "url": "https://aaoghumen.com/places",
            "containsPlace": destinations.map(dest => ({
              "@type": "TouristDestination",
              "name": dest.name,
              "description": dest.description,
              "url": `https://aaoghumen.com${dest.path}`,
              "containsPlace": dest.attractions.map(attr => ({
                "@type": "TouristAttraction",
                "name": attr.name,
                "description": attr.description,
                "url": `https://aaoghumen.com${attr.path}`
              }))
            }))
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-orange-500 to-red-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            All Tourist Places
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Discover the complete directory of destinations and attractions across Madhya Pradesh
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <span className="bg-white bg-opacity-20 px-4 py-2 rounded-full text-sm font-medium">
              {destinations.reduce((total, dest) => total + dest.attractions.length, 0)} Attractions
            </span>
            <span className="bg-white bg-opacity-20 px-4 py-2 rounded-full text-sm font-medium">
              {destinations.length} Destinations
            </span>
          </div>
        </div>
      </div>

      {/* Places Directory */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="space-y-16">
          {destinations.map((destination, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-xl overflow-hidden">
              {/* Destination Header */}
              <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white p-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">{destination.name}</h2>
                    <p className="text-lg opacity-90 max-w-2xl">{destination.description}</p>
                  </div>
                  <button
                    onClick={() => navigate(destination.path)}
                    className="bg-white text-orange-600 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors hidden md:block"
                  >
                    Explore {destination.name}
                  </button>
                </div>
                <button
                  onClick={() => navigate(destination.path)}
                  className="bg-white text-orange-600 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors w-full mt-4 md:hidden"
                >
                  Explore {destination.name}
                </button>
              </div>

              {/* Attractions Grid */}
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-8">Popular Attractions</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {destination.attractions.map((attraction, attractionIndex) => (
                    <div
                      key={attractionIndex}
                      className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all duration-300 cursor-pointer border border-gray-200 hover:border-orange-300"
                      onClick={() => navigate(attraction.path)}
                    >
                      <h4 className="text-xl font-bold text-gray-900 mb-3">{attraction.name}</h4>
                      <p className="text-gray-600 mb-4 leading-relaxed">{attraction.description}</p>
                      <div className="flex items-center text-orange-600 font-semibold">
                        <span>Learn More</span>
                        <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl text-white p-8 md:p-12 text-center mt-16">
          <h3 className="text-3xl md:text-4xl font-bold mb-6">Ready to Explore?</h3>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Plan your perfect trip to these amazing destinations with our expert guides
          </p>
          <button
            onClick={() => navigate('/onboarding')}
            className="bg-white text-orange-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Start Planning Your Trip
          </button>
        </div>

        {/* SEO Content */}
        <div className="mt-16 prose prose-lg max-w-none">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Why Visit These Places?</h2>
          <div className="grid md:grid-cols-2 gap-8 text-gray-700">
            <div>
              <h3 className="text-xl font-semibold mb-4">Rich Cultural Heritage</h3>
              <p className="mb-6">
                Madhya Pradesh is home to some of India's most significant cultural and religious sites. 
                From the spiritual significance of Ujjain's ancient temples to Indore's royal palaces, 
                each destination offers a unique glimpse into India's rich history.
              </p>
              
              <h3 className="text-xl font-semibold mb-4">Architectural Marvels</h3>
              <p>
                Experience diverse architectural styles from different eras - Maratha palaces, 
                Jain temples with intricate glasswork, and ancient Hindu temples that have 
                stood the test of time.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">Spiritual Significance</h3>
              <p className="mb-6">
                Many of these places hold deep spiritual importance, especially Ujjain, 
                which is one of the four sacred cities where Kumbh Mela is held. 
                The Mahakaleshwar Jyotirlinga attracts millions of devotees annually.
              </p>
              
              <h3 className="text-xl font-semibold mb-4">Culinary Delights</h3>
              <p>
                Don't miss the food culture, especially in Indore's Sarafa Bazaar, 
                where you can taste authentic local delicacies and street food that 
                has been perfected over generations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Places;